import { InvestorJourneyRes } from '@dealstack/shared/lib/generated';
import {
	JourneyIndicatorKey,
	JourneyIndicatorStatus,
	JourneyKind,
} from '@dealstack/shared/lib/common/business/investorJourneys';
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n.ts';
import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types.ts';
import { PredefinedIcons } from '@dealstack/frontend/shared/components/fundament/Icon/Icon.types.ts';
import { MIP_MANAGE_ROUTES } from '@dealstack/frontend/app/pages/Routing/routes.ts';
import {
	investorDetailsPath,
	mipManagePath,
} from '@dealstack/frontend/app/pages/Routing/routes.utils.ts';
import { buildTableFiltersSearch } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableDataOperationsFromUrlParamState.ts';
import { getInvestmentType } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageInvestments/utils/investment.helper.ts';
import { ActiveJourney } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/ActiveJourneysCard/activeJourneys.types.ts';

export const getIndicatorIcon = (status: JourneyIndicatorStatus): PredefinedIcons => {
	switch (status) {
		case JourneyIndicatorStatus.COMPLETED:
			return 'circle-check';
		case JourneyIndicatorStatus.IN_PROGRESS:
			return 'circle-progress';
		default:
			return 'circle-dot';
	}
};

export const getIndicatorColor = (status: JourneyIndicatorStatus): string => {
	switch (status) {
		case JourneyIndicatorStatus.COMPLETED:
			return 'teal.8';
		case JourneyIndicatorStatus.IN_PROGRESS:
			return 'orange.6';
		default:
			return 'gray.8';
	}
};

const INDICATOR_LABEL_KEYS: Record<JourneyIndicatorKey, string> = {
	[JourneyIndicatorKey.KYC]: 'kyc',
	[JourneyIndicatorKey.CONTRACTS]: 'contracts',
	[JourneyIndicatorKey.PAYMENTS]: 'payments',
	[JourneyIndicatorKey.PROOFS]: 'proofs',
	[JourneyIndicatorKey.DECLARATIONS]: 'declarations',
	[JourneyIndicatorKey.FORMS]: 'forms',
	[JourneyIndicatorKey.INVESTOR_SIGNATURES]: 'investor_signatures',
	[JourneyIndicatorKey.COUNTERPARTY_SIGNATURES]: 'counterparty_signatures',
};

interface Props {
	journeys: InvestorJourneyRes[] | undefined;
	dealId: string;
	mipId: string;
	investorId: string;
	canOpenInvestmentDetails: boolean;
	tMipManage: TranslateFunction<I18Namespaces.MIP_MANAGE>;
}

export const mapJourneysToCards = ({
	journeys,
	dealId,
	mipId,
	investorId,
	canOpenInvestmentDetails,
	tMipManage,
}: Props): ActiveJourney[] =>
	(journeys ?? []).map((journey) => {
		const indicators = journey.indicators.map((indicator) => ({
			key: indicator.key,
			label: tMipManage(
				`investors.details.active_journeys.indicators.${INDICATOR_LABEL_KEYS[indicator.key]}`
			),
			counts: indicator.counts,
		}));

		const base = { id: journey.id, icon: 'clock-time-watch' as PredefinedIcons, indicators };

		if (journey.kind === JourneyKind.INVESTMENT) {
			return {
				...base,
				type: tMipManage('investors.details.active_journeys.types.investment'),
				descriptor: journey.investmentType
					? getInvestmentType(journey.investmentType, tMipManage)
					: '',
				to: canOpenInvestmentDetails
					? mipManagePath(MIP_MANAGE_ROUTES.INVESTMENT_DETAILS, {
							dealId,
							mipId,
							investmentId: journey.id,
						})
					: `${mipManagePath(MIP_MANAGE_ROUTES.INVESTMENTS, {
							dealId,
							mipId,
						})}?${buildTableFiltersSearch([{ id: 'investorId', value: investorId }])}`,
			};
		}

		if (journey.kind === JourneyKind.STANDALONE_KYC) {
			return {
				...base,
				type: tMipManage('investors.details.active_journeys.types.standalone_kyc'),
				descriptor: tMipManage('investors.details.active_journeys.descriptors.kyc'),

				to: `${mipManagePath(`${MIP_MANAGE_ROUTES.KYC_PAGE}/main/:investorId`, {
					dealId,
					mipId,
					investorId,
				})}?${new URLSearchParams({
					kycWorkflowId: journey.id,
					kycEntryPointUrl: investorDetailsPath({
						dealId,
						mipId,
						investorId,
					}),
				}).toString()}`,
			};
		}

		return {
			...base,
			type: tMipManage('investors.details.active_journeys.types.document'),
			descriptor: journey.documentName ?? '',
			to: mipManagePath(MIP_MANAGE_ROUTES.DOCUMENT_DETAILS, {
				dealId,
				mipId,
				documentWorkflowInstanceId: journey.id,
			}),
		};
	});
